import { useState } from "react";
import { AlertTriangle, BellRing, CheckCircle2, Mail, Send } from "lucide-react";
import { apiRequest } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export default function AdminPanel() {
  const { user } = useAuth();
  const [smtpForm, setSmtpForm] = useState({ to: user.email, subject: "Test notifica DVR ProSuite", message: "Configurazione SMTP verificata." });
  const [smtpResult, setSmtpResult] = useState(null);
  const [smtpBusy, setSmtpBusy] = useState(false);
  const [remindersResult, setRemindersResult] = useState(null);
  const [remindersBusy, setRemindersBusy] = useState(false);
  const [error, setError] = useState("");

  const canRun = ["SUPER_ADMIN", "CONSULENTE"].includes(user.role);

  const testSmtp = async (e) => {
    e.preventDefault();
    setError(""); setSmtpResult(null); setSmtpBusy(true);
    try { setSmtpResult(await apiRequest("/notifications/test", { method: "POST", body: JSON.stringify(smtpForm) })); }
    catch (err) { setError(err.message); }
    finally { setSmtpBusy(false); }
  };

  const runReminders = async () => {
    setError(""); setRemindersResult(null); setRemindersBusy(true);
    try { setRemindersResult(await apiRequest("/notifications/run-deadlines", { method: "POST" })); }
    catch (err) { setError(err.message); }
    finally { setRemindersBusy(false); }
  };

  if (!canRun) {
    return <Alert variant="destructive" data-testid="admin-no-access"><AlertTriangle className="h-4 w-4" /><AlertTitle>Accesso negato</AlertTitle><AlertDescription>Pannello riservato a SUPER_ADMIN e CONSULENTE.</AlertDescription></Alert>;
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Strumenti amministrativi</p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950">Pannello admin</h2>
        <p className="mt-2 text-slate-600">Verifica integrazioni notifiche e lancia manualmente job di compliance.</p>
      </section>

      {error && <Alert variant="destructive" data-testid="admin-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}

      <Card className="shadow-none" data-testid="admin-smtp-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2"><Mail className="h-5 w-5 text-blue-900" /> Test integrazione SMTP</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600">Invia un&apos;email di prova al destinatario per verificare la configurazione SMTP. Senza variabili d&apos;ambiente <code className="rounded bg-slate-100 px-1 text-xs">SMTP_HOST</code>, <code className="rounded bg-slate-100 px-1 text-xs">SMTP_FROM</code>, <code className="rounded bg-slate-100 px-1 text-xs">SMTP_USER</code>, <code className="rounded bg-slate-100 px-1 text-xs">SMTP_PASS</code> configurate il sistema risponde con <code className="text-xs">smtp_configured: false</code> e non invia nulla.</p>
          <form onSubmit={testSmtp} className="grid gap-3 md:grid-cols-2" data-testid="admin-smtp-form">
            <div className="space-y-2"><Label>Destinatario</Label><Input type="email" value={smtpForm.to} onChange={(e) => setSmtpForm({ ...smtpForm, to: e.target.value })} required data-testid="admin-smtp-to-input" /></div>
            <div className="space-y-2"><Label>Oggetto</Label><Input value={smtpForm.subject} onChange={(e) => setSmtpForm({ ...smtpForm, subject: e.target.value })} data-testid="admin-smtp-subject-input" /></div>
            <div className="space-y-2 md:col-span-2"><Label>Messaggio</Label><Textarea rows={3} value={smtpForm.message} onChange={(e) => setSmtpForm({ ...smtpForm, message: e.target.value })} data-testid="admin-smtp-message-input" /></div>
            <Button type="submit" className="bg-blue-900 hover:bg-blue-800 md:col-span-2" disabled={smtpBusy} data-testid="admin-smtp-submit"><Send className="h-4 w-4" /> {smtpBusy ? "Invio..." : "Invia test"}</Button>
          </form>
          {smtpResult && (
            <Alert className={smtpResult.sent ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-200 bg-amber-50 text-amber-900"} data-testid="admin-smtp-result">
              {smtpResult.sent ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              <AlertTitle>{smtpResult.sent ? "Email inviata" : "Email non inviata"}</AlertTitle>
              <AlertDescription>
                <div className="space-y-1">
                  <p data-testid="admin-smtp-detail">{smtpResult.detail || "—"}</p>
                  <Badge className={smtpResult.smtp_configured ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"} data-testid="admin-smtp-configured-badge">SMTP {smtpResult.smtp_configured ? "configurato" : "non configurato"}</Badge>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-none" data-testid="admin-reminders-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2"><BellRing className="h-5 w-5 text-blue-900" /> Trigger notifiche scadenze</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600">Lancia manualmente il job di reminder scadenze. Lo scheduler ricorrente (cron <code className="rounded bg-slate-100 px-1 text-xs">DEADLINE_REMINDERS_CRON</code>, default lunedì 08:00 Europe/Rome) parte solo se SMTP è configurato e <code className="rounded bg-slate-100 px-1 text-xs">DEADLINE_REMINDERS_ENABLED</code> non è settato a false.</p>
          <Button className="bg-blue-900 hover:bg-blue-800" onClick={runReminders} disabled={remindersBusy} data-testid="admin-reminders-run-button"><Send className="h-4 w-4" /> {remindersBusy ? "Esecuzione..." : "Esegui job ora"}</Button>
          {remindersResult && (
            <Alert className="border-slate-200" data-testid="admin-reminders-result">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Job completato</AlertTitle>
              <AlertDescription>
                <div className="mt-1 flex flex-wrap gap-2">
                  <Badge className="bg-blue-50 text-blue-900" data-testid="admin-reminders-sent">{remindersResult.sent} email inviate</Badge>
                  <Badge className={remindersResult.smtp_configured ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"} data-testid="admin-reminders-smtp">SMTP {remindersResult.smtp_configured ? "configurato" : "non configurato"}</Badge>
                  <Badge className={remindersResult.scheduler_enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"} data-testid="admin-reminders-scheduler">Scheduler {remindersResult.scheduler_enabled ? "attivo" : "disattivato"}</Badge>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-none" data-testid="admin-api-docs-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2"><BellRing className="h-5 w-5 text-blue-900" /> Documentazione API</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600">Documentazione OpenAPI interattiva delle API REST di DVR ProSuite (82+ endpoint).</p>
          <div className="flex flex-wrap gap-3">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/docs`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-900 hover:bg-blue-100"
              data-testid="admin-swagger-link"
            >
              Swagger UI →
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/redoc`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="admin-redoc-link"
            >
              ReDoc →
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/openapi.json`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              data-testid="admin-openapi-json-link"
            >
              openapi.json
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
